import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Pencil, Search, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Field, SetupEditor, SetupPage } from './setup-shell';
import {
  useCreateHost,
  useDeleteHost,
  useHosts,
  useUpdateHost,
  useUserSearch,
  type BookingHost,
  type SearchedUser,
} from './setup.api';

/**
 * Hosts — the people who run time slots.
 *
 * A host *is* a user account plus a description, which is why creating one
 * starts with a search and editing one cannot change who it is: the API takes
 * `userId` on create and only `id` on update.
 */

export function HostsPage() {
  const { t } = useTranslation();

  const list = useHosts();
  const create = useCreateHost();
  const update = useUpdateHost();
  const remove = useDeleteHost();
  const userSearch = useUserSearch();

  const [editing, setEditing] = useState<BookingHost | null>(null);
  const [description, setDescription] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [picked, setPicked] = useState<SearchedUser | null>(null);

  const rows = list.data ?? [];
  const isEdit = !!editing?.id;
  const saving = create.isPending || update.isPending;

  function open(row: BookingHost | null) {
    setEditing(row ?? ({ id: '' } as BookingHost));
    setDescription(row?.description ?? '');
    setPicked(null);
    setUserQuery('');
    userSearch.reset();
  }

  const failed = () => toast.error(t('bookingSetup.actionFailed'));
  const saved = () => {
    toast.success(t('bookingSetup.saved'));
    setEditing(null);
  };

  function save() {
    if (isEdit) {
      update.mutate(
        {
          id: editing!.id,
          description,
          // Not editable here, but the model requires it, so send it back
          // rather than resetting the host's rating to zero on every save.
          rating: editing!.rating ?? 0,
        },
        { onSuccess: saved, onError: failed }
      );
    } else {
      create.mutate(
        { userId: picked!.id, description },
        { onSuccess: saved, onError: failed }
      );
    }
  }

  const hostName = (row: BookingHost) =>
    [row.firstName, row.lastName].filter(Boolean).join(' ') || row.name || '—';

  return (
    <SetupPage
      title={t('bookingSetup.hostsTitle')}
      description={t('bookingSetup.hostsSubtitle')}
      onCreate={() => open(null)}
      onReload={() => void list.refetch()}
      reloading={list.isFetching}
      isPending={list.isPending}
      isError={list.isError}
      errorMessage={t('bookingSetup.loadFailed')}
      isEmpty={list.isSuccess && rows.length === 0}
      emptyMessage={t('bookingSetup.noHosts')}
    >
      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              list.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div key={row.id} className="flex items-center gap-3 p-3">
                <Avatar className="size-10 shrink-0">
                  {row.avatar && <AvatarImage src={row.avatar} alt="" />}
                  <AvatarFallback>
                    {initials(row.firstName, row.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{hostName(row)}</p>
                  {row.email && (
                    <p className="text-muted-foreground truncate text-xs">
                      {row.email}
                    </p>
                  )}
                  {row.description && (
                    <p className="text-muted-foreground truncate text-xs">
                      {row.description}
                    </p>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => open(row)}>
                  <Pencil className="size-3.5" />
                  {t('bookingSetup.edit')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <SetupEditor
        title={isEdit ? t('bookingSetup.editHost') : t('bookingSetup.newHost')}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onSave={save}
        saving={saving}
        canSave={isEdit || !!picked}
        isEdit={isEdit}
        deleting={remove.isPending}
        deletePrompt={t('bookingSetup.deleteHostPrompt')}
        onDelete={() =>
          remove.mutate(editing!.id, {
            onSuccess: () => {
              toast.success(t('bookingSetup.deleted'));
              setEditing(null);
            },
            onError: failed,
          })
        }
      >
        <Field
          label={t('bookingSetup.user')}
          hint={isEdit ? t('bookingSetup.userNotEditable') : undefined}
        >
          {isEdit ? (
            <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
              <Avatar className="size-9 shrink-0">
                {editing?.avatar && (
                  <AvatarImage src={editing.avatar} alt="" />
                )}
                <AvatarFallback>
                  {initials(editing?.firstName, editing?.lastName)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm">{hostName(editing!)}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {editing?.email}
                </p>
              </div>
            </div>
          ) : picked ? (
            <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
              <Avatar className="size-9 shrink-0">
                {picked.avatar && <AvatarImage src={picked.avatar} alt="" />}
                <AvatarFallback>
                  {initials(picked.firstName, picked.lastName)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{picked.completeName}</p>
                <p className="text-muted-foreground truncate text-xs">
                  {picked.email}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t('bookingSetup.remove')}
                onClick={() => setPicked(null)}
              >
                <X className="size-4" />
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <form
                role="search"
                className="flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (userQuery.trim()) userSearch.mutate(userQuery.trim());
                }}
              >
                <div className="relative flex-1">
                  <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <Input
                    value={userQuery}
                    onChange={(e) => setUserQuery(e.target.value)}
                    placeholder={t('bookingSetup.searchUserPlaceholder')}
                    aria-label={t('bookingSetup.searchUserPlaceholder')}
                    className="pl-9"
                  />
                </div>
                <Button type="submit" disabled={userSearch.isPending}>
                  {userSearch.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    t('common.search')
                  )}
                </Button>
              </form>

              {userSearch.isSuccess && userSearch.data.length === 0 && (
                <p className="text-muted-foreground text-xs">
                  {t('bookingSetup.noUsersFound')}
                </p>
              )}

              {(userSearch.data ?? []).length > 0 && (
                <div className="max-h-56 divide-y overflow-y-auto rounded-xl border">
                  {userSearch.data!.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left transition-colors"
                      onClick={() => {
                        setPicked(user);
                        userSearch.reset();
                        setUserQuery('');
                      }}
                    >
                      <Avatar className="size-8 shrink-0">
                        {user.avatar && (
                          <AvatarImage src={user.avatar} alt="" />
                        )}
                        <AvatarFallback>
                          {initials(user.firstName, user.lastName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate text-sm">{user.completeName}</p>
                        <p className="text-muted-foreground truncate text-xs">
                          {user.email}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Field>

        <Field label={t('bookingSetup.description')}>
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </SetupEditor>
    </SetupPage>
  );
}
